import React, { useMemo } from 'react';
import styled from 'styled-components';
import { LFC } from '../../../Legacy';
import { JiraIssueData } from '../../../api/endpoints/jira';
import { Icon } from '../../../concepts/resources/Icon';
import { Rect } from '../../../platform/geom/Rect';
import { Colors } from '../../../ui';
import { RenderBounds, RenderBox, RenderGraffiti, RenderInner, RenderPressable, usePose, usePressableContext } from '../../canvas/visualization/RenderHelpers';
import { RenderText } from '../../canvas/visualization/RenderText';
import { JiraCard, JiraCardCustomInfo } from './JiraCard';
import { JiraCardLayout } from './JiraCardLayout';
import { JiraSummaryTextSettings } from './fields/JiraSummaryField';

const resolveColor = (color: string) => {
    return (
        (color === 'blue-gray' ? Colors.slate300 : null) ??
        Colors.normalize(color) ??
        Colors.normalize((color || '').split('-')[0])
    );
};

const usePoseDerivatives = (object: JiraCard) => {
    const p = usePose(object);
    return useMemo(() => ({
        highlighted: !!(p.pressed || p.targeted || p.selected) && !p.beingManipulated,
        selected: p.selected,
        editing: p.beingEdited,
    }), [p]);
};

type AspectProps = { area: Rect, record?: JiraIssueData };

const RenderDesign: LFC<{ layout: JiraCardLayout, highlighted: boolean, selected: boolean }> = ({ layout, highlighted, selected }) => {

    const border = (
        (selected ? Colors.primary : null) ??
        (highlighted ? Colors.primaryLight : null) ??
        null
    );

    return <>
        <RenderBox
            inset="0"
            border={`solid 1px ${Colors.border}`}
            borderBottom={`solid 4px ${Colors.border}`}
            borderRadius="10px"
            background="white"
        />
        <RenderBox
            {...layout.footer.toCSS()}
            borderTop={`solid 1px ${Colors.border}`}
        />
        {border && <RenderBox
            inset="0"
            border={`solid 2px ${border}`}
            borderRadius="10px"
        />}
    </>;
};

const RenderPlaceholder: LFC<{ area: Rect }> = ({ area }) => {
    return <RenderBox {...area.toCSS()} background={Colors.border} borderRadius="4px" />;
};

const RenderKey = React.memo(({ layout, value }: { layout: JiraCardLayout, value: string }) => {
    const style = {
        display: 'flex',
        alignItems: 'center',
        fontSize: '16px',
        color: Colors.primaryText,
    };
    return <RenderBox {...layout.key.toCSS()} {...style}>
        {value}
    </RenderBox>;
});

const RenderParent = React.memo<AspectProps & { force?: boolean }>(({ area, record, force }) => {
    if (!record?.parent?.id && !force) return null;
    const empty = !record?.parent?.id;
    const style = {
        padding: '2px 4px 1px',
        borderRadius: '2px',
        fontSize: '11px',
        fontWeight: 420,
        color: empty ? Colors.text : '#5E4DB2',
        background: empty ? Colors.hover : '#DFD8FD',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        textAlign: 'center',
    };
    return <RenderBox {...area.toCSS()} {...style}>
        {record?.parent?.summary?.toUpperCase() ?? 'SET EPIC'}
    </RenderBox>;
});

const RenderPriority = React.memo<AspectProps>(({ area, record }) => {
    if (!record?.priority?.id) return <RenderPlaceholder area={area} />;
    return <RenderBox {...area.toCSS()}
        backgroundImage={`url(${record.priority.iconUrl})`}
        backgroundSize="contain"
    />;
});

const RenderIssueType = React.memo<AspectProps>(({ area, record }) => {
    if (!record?.issueType?.id) return <RenderPlaceholder area={area} />;

    // iconUrl has at the time of writing ?size=medium on the end but we want ?format=svg
    const iconUrl = `${record.issueType.iconUrl?.split('?')[0]}?format=svg`;

    return <RenderBox {...area.toCSS()}
        backgroundImage={`url(${iconUrl})`}
        backgroundSize="contain"
    />;
});

const RenderStatus = React.memo<AspectProps>(({ area, record }) => {
    if (!record?.status?.id) return <RenderPlaceholder area={area} />;
    // const color = resolveColor(data?.statusCategory?.colorName || '');
    const style = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'end',
        fontSize: '16px',
        fontWeight: 400,
        color: Colors.primaryText,
        borderRadius: '4px',
    };
    return <RenderBox {...area.toCSS()} padding="0 2px" {...style}>
        {/* <RenderBox position="relative" display="inline-block" width="1em" height="1em" borderRadius="50%" background={color} /> */}
        {record.status.name}
    </RenderBox>;
});

const RenderAssignee = React.memo<AspectProps>(({ area, record }) => {
    if (!record?.assignee?.id) return <RenderPlaceholder area={area} />;
    return <RenderBox {...area.toCSS()}
        backgroundImage={`url(${record.assignee.avatarUrl})`}
        backgroundSize="contain"
        borderRadius="4px"
    />;
});

const RenderMore = React.memo<AspectProps>(({ area }) => {
    const state = usePressableContext();
    const background = (
        (state === 'active' ? Colors.active : null) ??
        (state === 'hover' ? Colors.hover : null) ??
        'none'
    );
    const style = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: Colors.contrastTextColorFor(background),
        background,
        border: `solid 1px ${state === 'idle' ? Colors.border : background}`,
        borderRadius: '4px',
    };
    return <RenderBox {...area.toCSS()} {...style} >
        <Icon size="1" src="deqo://icons/hero-mini/ellipsis-horizontal" />
    </RenderBox>;
});

const RenderCustomLabel = styled.div`
    position: absolute;
    top: -8px;
    left: 2px;
    font-size: 6px;
    color: ${Colors.textLighter};
`;

const RenderCustomValue = styled.div`
    position: absolute;
    inset: 0;
    font-size: 16px;
    font-weight: 400;
    text-align: center;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const RenderCustomFrame = ({ area, children, }: { area: Rect, children: React.ReactNode }) =>
    <RenderBox
        {...area.toCSS()}
        borderRadius="4px"
        background={Colors.gray100}
        color={Colors.text}
    >
        {children}
    </RenderBox>;

const RenderFallbackCustom = React.memo<{ area: Rect, custom: JiraCardCustomInfo }>(({ area, custom }) => {
    if (area.area() <= 0) return null;
    return <RenderCustomFrame area={area}>
        <RenderCustomLabel>{custom.name}:</RenderCustomLabel>
        <RenderCustomValue>{JiraCardCustomInfo.labelFor(custom) ?? '-'}</RenderCustomValue>
    </RenderCustomFrame>;
});

const RenderPrimitiveCustom = React.memo<{ area: Rect, editing: boolean, custom: JiraCardCustomInfo }>(({ area, editing, custom }) => {
    if (area.area() <= 0) return null;
    return <RenderCustomFrame area={area}>
        <RenderCustomLabel>{custom.name}:</RenderCustomLabel>
        <RenderCustomValue>{!editing && (JiraCardCustomInfo.labelFor(custom) ?? '-')}</RenderCustomValue>
    </RenderCustomFrame>;
});

const RenderSprintCustom = React.memo<{ area: Rect, custom: JiraCardCustomInfo }>(({ area, custom }) => {
    if (area.area() <= 0) return null;
    return <>
        <RenderCustomFrame area={area}>
            <RenderCustomLabel>Sprint:</RenderCustomLabel>
            <RenderCustomValue>{JiraCardCustomInfo.labelFor(custom) ?? '-'}</RenderCustomValue>
        </RenderCustomFrame>
    </>;
});

const typeMap = {
    default: RenderFallbackCustom,
    sprint: RenderSprintCustom,
    number: RenderPrimitiveCustom,
    string: RenderPrimitiveCustom,
};

const RenderCustom = React.memo<{ area: Rect, editing: boolean, custom: JiraCardCustomInfo }>(({ area, editing, custom }) => {
    if (!area?.area()) return null;
    const RenderType = typeMap[custom.type] ?? typeMap.default;
    return <RenderType {...{ area, editing, custom }} />;
});

// const RenderPoints: LFC<{ object: JiraCard }> = ({ object }) => {
//     const { points } = object.layout;
//     const style = {
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         fontSize: '12px',
//         fontWeight: 500,
//         color: Colors.text,
//         background: Colors.border,
//         borderRadius: '12px',
//     };
//     return <RenderBox {...points.toCSS()} {...style}>
//         {object.points}
//     </RenderBox>;
// });

export const JiraCardVisual: LFC<{ object: JiraCard }> = ({ object, children }) => {

    const { highlighted, selected, editing } = usePoseDerivatives(object);
    const { layout, record, customs } = object;

    return <>
        <RenderBounds object={object}>
            <RenderInner object={object}>
                <RenderDesign {...{ layout, highlighted, selected }} />
                <RenderGraffiti object={object} />
                <RenderKey layout={layout} value={object.key} />
                <RenderParent area={layout.parent} record={record} force={editing} />
                <RenderPriority area={layout.priority} record={record} />
                <RenderIssueType area={layout.issueType} record={record} />
                <RenderStatus area={layout.status} record={record} />
                <RenderAssignee area={layout.assignee} record={record} />
                <RenderPressable object={object} hint="more">
                    <RenderMore area={layout.more} />
                </RenderPressable>
                {/* <RenderPoints object={object} /> */}
                {!editing && <RenderText bounds={layout.summary} settings={JiraSummaryTextSettings} value={record?.summary || ''} />}
                {customs.map(x =>
                    <RenderCustom key={x.key} area={layout.customs[x.key]} editing={editing} custom={x} />
                )}
            </RenderInner>
        </RenderBounds >
    </>;
};
